import WrapClampSpan from "@/components/wrap-clamp-span";
import { cn } from "@/flokit/utils/styling/cn";
import { nanoid } from "nanoid";
import React, { ReactNode } from "react";
import { toast, ToastPosition } from "react-hot-toast";
import {
  FiAlertOctagon,
  FiAlertTriangle,
  FiCheck,
  FiInfo,
} from "react-icons/fi";
import { Button } from "../button/button";

interface ShowToastProps extends ToastProps {
  position?: ToastPosition;
  autoClose?: number;
  button?: ReactNode;
}

interface ToastProps {
  message: string;
  type: "success" | "error" | "info" | "warning";
  button?: ReactNode;
}

interface Id {
  id: string;
}

const BackgroundColors = {
  success: "bg-[#475063] text-white",
  info: "bg-white",
  warning: "bg-red-500",
  error: "bg-ui-red text-white",
};

const Icons = {
  success: FiCheck,
  info: FiInfo,
  warning: FiAlertTriangle,
  error: FiAlertOctagon,
};

const SECONDS = 1000;

function Toast(props: ToastProps & Id) {
  const bg = BackgroundColors[props.type];

  return (
    <span
      onMouseDownCapture={(e) => e.stopPropagation()}
      onTouchStartCapture={(e) => e.stopPropagation()}
      onPointerDownCapture={(e) => e.stopPropagation()}
      className={cn(
        "grid grid-cols-[auto,1fr,auto] items-center rounded-md px-3 py-2 font-medium shadow-lg",
        bg,
      )}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-black shadow">
        {React.createElement(Icons[props.type], {
          size: 16,
        })}
      </div>
      <WrapClampSpan
        wrappingThreshold={15}
        lineClamp="none"
        className="ml-3 pr-4 text-base"
      >
        {props.message}
      </WrapClampSpan>
      {props.button ? (
        props.button
      ) : (
        <Button
          onClick={() => {
            toast.dismiss(props.id);
          }}
          size="micro"
          color="secondary"
          UNSAFE_className="text-black"
        >
          Okay
        </Button>
      )}
    </span>
  );
}

export function showToast(
  argsOrMessage: ShowToastProps | string,
  type?: "success" | "info" | "warning" | "error",
): void {
  const props =
    typeof argsOrMessage === "string"
      ? { message: argsOrMessage ?? "", type: type ?? ("success" as const) }
      : argsOrMessage;

  const id = nanoid();
  toast(
    () => {
      return (
        <Toast
          message={props.message ?? ""}
          type={props.type}
          id={id}
          button={props.button}
        />
      );
    },
    {
      id,
      position: props.position ?? "bottom-right",
      duration:
        props.autoClose ??
        5 * SECONDS + (props?.message?.length / 120) * SECONDS,
    },
  );
}

// Custom Toasts have no default styling from react-hot-toasts
// https://github.com/timolins/react-hot-toast/blob/1f7005356bd419100541a7dcd38d79fad2a2314e/site/pages/docs/toast.mdx#custom-jsx
export function showCustomToast(props: ShowToastProps) {
  const id = nanoid();

  toast.custom(
    () => {
      return (
        <Toast
          message={props.message}
          type={props.type}
          id={id}
          button={props.button}
        />
      );
    },
    {
      id,
      position: props.position ?? "bottom-right",
      duration:
        props.autoClose ??
        5 * SECONDS + (props?.message?.length / 120) * SECONDS,
    },
  );
}
